import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const REGIME_BG = {
  bullish: "#4ADE80",
  mixed:   "#F5A623",
  weak:    "#FF5C5C",
  unknown: "#3a4742",
};
const REGIME_LABEL = {
  bullish: "UP TODAY",
  mixed: "FLAT TODAY",
  weak: "DOWN TODAY",
  unknown: "—",
};

export default function SectorStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSectors = React.useCallback(async ({ fresh = false } = {}) => {
    try {
      if (fresh) setRefreshing(true);
      const r = await api.get(fresh ? "/sectors?fresh=1" : "/sectors");
      setData(r.data);
      setErr(false);
    } catch {
      setErr(true);
    } finally {
      if (fresh) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    fetchSectors().finally(() => { if (!alive) return; });
    // Refresh every 2 minutes so an open dashboard stays in sync with the tape
    const interval = setInterval(() => { if (alive) fetchSectors(); }, 120000);
    // Listen for global "mb:refresh-tape" events so the Dashboard's Refresh
    // button can force a fresh sector recompute alongside the alert feed.
    const onGlobalRefresh = () => fetchSectors({ fresh: true });
    window.addEventListener("mb:refresh-tape", onGlobalRefresh);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("mb:refresh-tape", onGlobalRefresh);
    };
  }, [fetchSectors]);

  // Auto-expand the top-performing sector by change_pct on first load
  // (user request, Feb 22 2026). Only fires ONCE per data arrival — a
  // manual selection sticks; a manual DE-selection stays deselected on
  // subsequent auto-refreshes. Tracks by `data.sectors` identity so a
  // fresh fetch that changes the leader re-triggers the auto-select
  // only if the user hasn't interacted since.
  const [userTouched, setUserTouched] = useState(false);
  useEffect(() => {
    if (userTouched) return;
    const sectors = data?.sectors;
    if (!sectors?.length) return;
    // Highest daily change wins; ties broken by first-appearance order.
    const top = sectors.reduce((acc, s) => (
      (s.change_pct != null && (acc == null || s.change_pct > acc.change_pct)) ? s : acc
    ), null);
    if (top?.key) setSelected(top.key);
  }, [data?.sectors, userTouched]);

  // Skeleton — always render a row of placeholder pills. During off-hours,
  // cold-start, or a fetch failure, the section stays visible but greyed
  // out. Never let a whole strip disappear.
  if (!data?.sectors?.length) {
    const placeholderKeys = ["tech", "consumer", "energy", "health", "financials", "utilities", "materials", "industrials", "real-estate"];
    return (
      <div className="mt-3 opacity-70" data-testid="sector-strip-skeleton">
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {placeholderKeys.map((k) => (
            <div
              key={k}
              className="font-mono-data text-[10px] tracking-wider font-bold text-center animate-pulse"
              style={{
                backgroundColor: REGIME_BG.unknown,
                color: "#666",
                padding: "10px 12px",
                borderRadius: "0.375rem",
                letterSpacing: "0.16em",
              }}
            >
              —
            </div>
          ))}
        </div>
        <div className="text-[10px] font-mono-data mt-2 opacity-70" style={{ color: "var(--mb-ink-subtle)" }}>
          {err ? "SECTORS UNAVAILABLE" : "SCANNING SECTORS…"}
        </div>
      </div>
    );
  }

  const sectors = data.sectors;
  const active = selected ? sectors.find((s) => s.key === selected) : null;

  return (
    <div className="mt-3" data-testid="sector-strip">
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="sector-pills">
        {sectors.map((s) => {
          const isActive = selected === s.key;
          const bg = REGIME_BG[s.regime] || REGIME_BG.unknown;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setUserTouched(true);
                setSelected(isActive ? null : s.key);
              }}
              data-testid={`sector-pill-${s.key}`}
              aria-pressed={isActive}
              title={`${s.label} — ${REGIME_LABEL[s.regime] || "—"} (click for detail)`}
              className="font-mono-data text-[10px] tracking-wider font-bold transition-all"
              style={{
                backgroundColor: bg,
                color: "#030110",
                padding: "10px 12px",
                borderRadius: "0.375rem",
                border: "none",
                cursor: "pointer",
                letterSpacing: "0.16em",
                boxShadow: isActive
                  ? `0 0 0 1px #EEE9F5, 0 0 20px ${bg}aa, inset 0 1px 0 rgba(255,255,255,0.25)`
                  : `0 0 12px ${bg}44, inset 0 1px 0 rgba(255,255,255,0.18)`,
                transform: isActive ? "translateY(-2px)" : "none",
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      {/* Legend — makes it explicit that color is TODAY's tape, matching
          how a trader scans the market on the open. */}
      <div className="mt-2 text-[10px] font-mono-data tracking-wide flex items-center gap-3 flex-wrap" style={{ color: "var(--mb-ink-subtle)" }}>
        <span className="opacity-70">COLOR = TODAY:</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: REGIME_BG.bullish }} /> UP</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: REGIME_BG.mixed }} /> FLAT</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: REGIME_BG.weak }} /> DOWN</span>
        <span className="opacity-50">· click a pill for today&apos;s ETF change + why</span>
      </div>

      {active && (
        <div
          className="mt-3 mb-card p-5 fade-in-up"
          data-testid={`sector-summary-${active.key}`}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <span
              className="label-uppercase shrink-0"
              style={{
                backgroundColor: REGIME_BG[active.regime] || REGIME_BG.unknown,
                color: "#030110",
                padding: "6px 12px",
                borderRadius: "0.375rem",
                boxShadow: `0 0 16px ${(REGIME_BG[active.regime] || REGIME_BG.unknown)}66`,
                letterSpacing: "0.16em",
                fontWeight: 700,
              }}
            >
              {active.label} · {REGIME_LABEL[active.regime] || "—"}
            </span>
            <div className="font-mono-data text-[11px] text-right" style={{ color: "var(--mb-ink-subtle)" }}>
              {active.etf} <span style={{ color: "var(--mb-ink)" }}>${active.price}</span>
              {active.change_pct != null && (
                <span className="ml-2" style={{ color: active.change_pct >= 0 ? "#4ADE80" : "#FF5C5C" }}>
                  {active.change_pct >= 0 ? "+" : ""}{active.change_pct}%
                </span>
              )}
            </div>
          </div>
          {/* Narrative — the "what's happening here" line, not indicator dump */}
          <p
            className="mt-3 text-[15px] leading-relaxed"
            style={{ color: "var(--mb-ink)" }}
            data-testid={`sector-highlight-${active.key}`}
          >
            {active.highlight || active.blurb}
          </p>
          {/* Small tape tag underneath — drivers (if the LLM cited any) + the numbers */}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mono-data tracking-wide" style={{ color: "var(--mb-ink-subtle)" }}>
            {(active.drivers || []).length > 0 && (
              <>
                <span>DRIVERS:</span>
                {active.drivers.map((d) => (
                  <Link
                    key={d}
                    to={`/app/stock/${d}`}
                    className="px-1.5 py-0.5 rounded font-bold hover:brightness-125 transition"
                    style={{ background: "var(--mb-surface-elevated)", color: "var(--mb-ink)", border: "1px solid var(--mb-border)" }}
                    data-testid={`sector-driver-${d}`}
                  >
                    {d}
                  </Link>
                ))}
                <span className="opacity-40">·</span>
              </>
            )}
            {active.tape_tag && <span>{active.tape_tag}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
